/**
 * ToolNav — persistent cross-tool navigation for the app bar. Lets a user jump
 * straight to any of the six core SiteOperations tools from wherever they are,
 * without detouring back through the Tool Hub. The tool the user is currently in
 * is highlighted and rendered non-interactive.
 *
 * Rendered once inside the shared <AppBar> (covers eReg, Site Intelligence, Site
 * Organizer, IXVerify, MHR, LMS, 24-Hour Line) and, separately, inside the CRM
 * and Status Board bars, which roll their own header. The tool set is a
 * deliberately curated constant — not the full /api/my-tools catalog — so it
 * stays tidy (per the owner's request; the 24-Hour Line entry was added by
 * docs/plans/oncall-line-plan.md WP-03). Routes mirror App.tsx.
 *
 * On-color styling: the app bar is the system's one blue gradient, so the inline
 * links are white-based in both themes; only the dropdown panel is a light/dark
 * surface. Responsive: inline links from xl up; a compact "Tools" dropdown below
 * xl and on mobile, matching the density system's laptop-first primary band. The
 * inline row is flex-1 + min-w-0 with hidden overflow-scroll so it yields space
 * gracefully rather than shove the bar's right-hand controls off a cramped bar.
 */
import { useEffect, useRef, useState } from 'react';
import type { ComponentType, MouseEvent as ReactMouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  Brain,
  FolderOpen,
  GraduationCap,
  PhoneCall,
  LayoutGrid,
  ChevronDown,
  Check,
} from 'lucide-react';

/**
 * The Lead CRM funnel mark (mirrors crm/components/CrmMark) — kept local so the
 * shared ui/ library carries no dependency on a feature module.
 */
function CrmFunnelMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect x="3" y="4.4" width="18" height="3.6" rx="1.8" fill="currentColor" />
      <rect x="6.5" y="10.2" width="11" height="3.6" rx="1.8" fill="currentColor" opacity="0.78" />
      <rect x="10" y="16" width="4" height="3.6" rx="1.8" fill="currentColor" opacity="0.56" />
    </svg>
  );
}

interface ToolDef {
  key: string;
  /** Full name — used in the dropdown. */
  label: string;
  /** Trimmed name — used in the tighter inline row. */
  short: string;
  route: string;
  Icon: ComponentType<{ className?: string }>;
}

// Curated, ordered set — the tools the owner wants reachable everywhere.
const TOOLS: ToolDef[] = [
  { key: 'status-board', label: 'Status Board', short: 'Status Board', route: '/status-board', Icon: ClipboardList },
  { key: 'ereg', label: 'eReg', short: 'eReg', route: '/ereg', Icon: FileText },
  { key: 'intelligence', label: 'Site Intelligence', short: 'Intelligence', route: '/intelligence', Icon: Brain },
  { key: 'crm', label: 'CRM', short: 'CRM', route: '/crm', Icon: CrmFunnelMark },
  { key: 'organizer', label: 'Site Organizer', short: 'Organizer', route: '/siteorganizer', Icon: FolderOpen },
  { key: 'lms', label: 'Learning Center', short: 'Learning', route: '/lms', Icon: GraduationCap },
  { key: 'oncall', label: '24-Hour Line', short: '24-Hr Line', route: '/oncall', Icon: PhoneCall },
];

/** A tool is "current" when the path is its route or nested under it. */
function isCurrent(pathname: string, route: string) {
  return pathname === route || pathname.startsWith(route + '/');
}

interface ToolNavProps {
  /**
   * Optional navigation interceptor. The CRM passes one to guard an active call
   * before leaving (preventDefault + open its confirm modal); other bars omit
   * it and the plain <Link> navigation runs.
   */
  onNavigate?: (to: string, e: ReactMouseEvent) => void;
}

export default function ToolNav({ onNavigate }: ToolNavProps) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClick = (to: string) => (e: ReactMouseEvent) => {
    onNavigate?.(to, e);
    setOpen(false);
  };

  const inlineBase =
    'inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-white/60';

  return (
    <>
      {/* Inline links — laptop/desktop (xl+). flex-1 fills the gap so the bar's
          right-hand controls stay pinned right; min-w-0 + hidden overflow-scroll
          lets the row give way instead of overflowing a cramped bar. */}
      <nav
        aria-label="Switch tool"
        className="hidden min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] xl:flex [&::-webkit-scrollbar]:hidden"
      >
        {TOOLS.map(({ key, short, route, Icon }) => {
          const current = isCurrent(pathname, route);
          return current ? (
            <span key={key} aria-current="page" className={`${inlineBase} bg-white/15 text-white`}>
              <Icon className="h-4 w-4 flex-shrink-0" />
              {short}
            </span>
          ) : (
            <Link
              key={key}
              to={route}
              onClick={handleClick(route)}
              className={`${inlineBase} text-white/80 hover:bg-white/10 hover:text-white`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {short}
            </Link>
          );
        })}
      </nav>

      {/* Collapsed dropdown — narrow / mobile (below xl). mr-auto keeps it beside
          the tool title with the bar's right-hand controls pinned right. */}
      <div ref={menuRef} className="relative mr-auto xl:hidden">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Switch tool"
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
        >
          <LayoutGrid className="h-4 w-4" />
          <span className="hidden sm:inline">Tools</span>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            role="menu"
            aria-label="Switch tool"
            className="absolute left-0 top-full z-40 mt-1 w-56 rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          >
            {TOOLS.map(({ key, label, route, Icon }) => {
              const current = isCurrent(pathname, route);
              return (
                <Link
                  key={key}
                  to={route}
                  role="menuitem"
                  onClick={handleClick(route)}
                  aria-current={current ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    current
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 flex-shrink-0 ${
                      current ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  />
                  <span className="flex-1 truncate">{label}</span>
                  {current && <Check className="h-4 w-4 flex-shrink-0" />}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
